import { useEffect, useMemo, useState } from "react";
import "../../styles/pl.css";
import {
  fetchPl,
  fetchPlAds,
  fetchPlFinance,
  fetchPlImports,
  fetchPlProducts,
  isMockMode,
  ApiClientError,
} from "../../api/client";
import type { FinanceBasis, FinanceResponse, Mall, PlAdsResponse, PlImportsResponse, PlProductsResponse, PlResponse } from "../../types/pl";
import { chartRange, compareRange, mainRange, type PeriodMode } from "../../lib/plChart";
import { jstTodayYmd } from "../../lib/dateRange";
import { PlPeriodBar } from "./PlPeriodBar";
import { OverviewTab } from "./tabs/OverviewTab";
import { MallTab } from "./tabs/MallTab";
import { ProductTab } from "./tabs/ProductTab";
import { AdsTab } from "./tabs/AdsTab";
import { ImportTab } from "./tabs/ImportTab";
import { FinanceTab } from "./tabs/FinanceTab";
import { StateBanner } from "../../components/StateBanner";

export type PlTab = "overview" | "mall" | "product" | "ads" | "finance" | "import";

/**
 * mockモード（`?mock=1`）のときだけfixtureの最終日（2026-09-23＝briefing基準日）に固定する。
 * 実運用では JST の実日付（`jstTodayYmd()`）を使い、GET /pl の `meta.data_updated_at` が
 * 取得できたらその日付に更新する（review §4-1: PL_TODAYハードコード廃止）。
 */
const FIXTURE_TODAY = "2026-09-23";

function computeInitialToday(): string {
  return isMockMode() ? FIXTURE_TODAY : jstTodayYmd();
}

const PL_MIN_DATE = "2026-01-01";
/** 財務タブ（freee）は会計期間の期首（2026-06-01）以降のみ。会計年度は 6月〜翌5月。 */
const FINANCE_MIN_DATE = "2026-06-01";
const FINANCE_MODES: PeriodMode[] = ["month"];

const TABS: { key: PlTab; label: string }[] = [
  { key: "overview", label: "概要" },
  { key: "mall", label: "モール" },
  { key: "product", label: "商品" },
  { key: "ads", label: "広告" },
  { key: "finance", label: "財務" },
  { key: "import", label: "取込" },
];

interface PlState {
  tab: PlTab;
  mode: PeriodMode;
  day: string;
  month: string;
  from: string;
  to: string;
  mall: Mall | "all";
  /** 財務タブの計上基準。null = 未指定（accrual データがあれば発生ベース、無ければ入金ベース） */
  basis: FinanceBasis | null;
}

const BASIS_STORAGE_KEY = "stoq-pl-finance-basis";

function parseBasis(v: string | null | undefined): FinanceBasis | null {
  return v === "deposit" || v === "accrual" ? v : null;
}

function readStoredBasis(): FinanceBasis | null {
  try {
    return parseBasis(window.localStorage.getItem(BASIS_STORAGE_KEY));
  } catch {
    return null;
  }
}

function storeBasis(b: FinanceBasis): void {
  try {
    window.localStorage.setItem(BASIS_STORAGE_KEY, b);
  } catch {
    /* localStorage 不可（プライベートウィンドウ等）は無視 */
  }
}

function readUrlState(today: string): PlState {
  const p = new URLSearchParams(window.location.search);
  const tab = (p.get("pl") as PlTab) || "overview";
  const mode = (p.get("mode") as PeriodMode) || "month";
  return {
    tab: TABS.some((t) => t.key === tab) ? tab : "overview",
    mode: mode === "day" || mode === "range" ? mode : "month",
    day: p.get("day") || today,
    month: p.get("month") || today.slice(0, 7),
    from: p.get("from") || "2026-08-01",
    to: p.get("to") || today,
    mall: (p.get("mall") as Mall | "all") || "all",
    basis: parseBasis(p.get("basis")) ?? readStoredBasis(),
  };
}

function writeUrlState(s: PlState): void {
  const p = new URLSearchParams(window.location.search);
  p.set("pl", s.tab);
  p.set("mode", s.mode);
  if (s.mode === "day") p.set("day", s.day);
  if (s.mode === "month") p.set("month", s.month);
  if (s.mode === "range") {
    p.set("from", s.from);
    p.set("to", s.to);
  }
  if (s.mall !== "all") p.set("mall", s.mall);
  else p.delete("mall");
  if (s.tab === "finance" && s.basis) p.set("basis", s.basis);
  else p.delete("basis");
  const url = `${window.location.pathname}?${p.toString()}`;
  window.history.replaceState(null, "", url);
}

type Loadable<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: T };

export function PlPage() {
  const [today, setToday] = useState<string>(() => computeInitialToday());
  const [state, setState] = useState<PlState>(() => readUrlState(today));
  const [hoverDay, setHoverDay] = useState<string | null>(null);

  useEffect(() => writeUrlState(state), [state]);

  // ---- 財務タブは月次のみ（URL ?mode=day 等で来ても月次に戻す）。期首より前の月も期首に丸める ----
  useEffect(() => {
    if (state.tab !== "finance") return;
    const minMonth = FINANCE_MIN_DATE.slice(0, 7);
    if (state.mode !== "month" || state.month < minMonth) {
      setState((s) => ({ ...s, mode: "month", month: s.month < minMonth ? minMonth : s.month }));
    }
  }, [state.tab, state.mode, state.month]);

  const main = useMemo(
    () => mainRange(state.mode, { day: state.day, month: state.month, from: state.from, to: state.to }, today),
    [state.mode, state.day, state.month, state.from, state.to, today]
  );
  const isFinance = state.tab === "finance";
  const compare = useMemo(
    () => (isFinance ? null : compareRange(state.mode, { month: state.month }, main)),
    [isFinance, state.mode, state.month, main]
  );
  const chart = useMemo(() => chartRange(state.mode, { day: state.day, month: state.month }, main), [state.mode, state.day, state.month, main]);

  // ---- 概要/モール/商品 共通の GET /pl（KPI・P/L表・モールカード・日別表・固定費/side_costs） ----
  const [plMain, setPlMain] = useState<Loadable<PlResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab === "import" || state.tab === "finance") return;
    setPlMain({ status: "loading" });
    fetchPl(main.from, main.to, compare ?? undefined)
      .then((data) => setPlMain({ status: "ok", data }))
      .catch((e: unknown) => setPlMain({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab, main.from, main.to, compare?.from, compare?.to]);

  // ---- 今日の基準日を meta.data_updated_at で補正（mockモードはfixture固定のまま動かさない） ----
  useEffect(() => {
    if (isMockMode()) return;
    if (plMain.status !== "ok") return;
    const d = plMain.data.meta.data_updated_at.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(d) && d !== today) setToday(d);
  }, [plMain, today]);

  // ---- 概要タブのみ: 売上チャート用に広めの daily を別取得（KPI期間とは別窓） ----
  const [plChartData, setPlChartData] = useState<Loadable<PlResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab !== "overview") return;
    setPlChartData({ status: "loading" });
    fetchPl(chart.from, chart.to)
      .then((data) => setPlChartData({ status: "ok", data }))
      .catch((e: unknown) => setPlChartData({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab, chart.from, chart.to]);

  // ---- 商品タブ ----
  const [plProducts, setPlProducts] = useState<Loadable<PlProductsResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab !== "product") return;
    setPlProducts({ status: "loading" });
    fetchPlProducts(main.from, main.to, state.mall)
      .then((data) => setPlProducts({ status: "ok", data }))
      .catch((e: unknown) => setPlProducts({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab, main.from, main.to, state.mall]);

  // ---- 広告タブ ----
  const [plAds, setPlAds] = useState<Loadable<PlAdsResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab !== "ads") return;
    setPlAds({ status: "loading" });
    fetchPlAds(main.from, main.to, state.mall)
      .then((data) => setPlAds({ status: "ok", data }))
      .catch((e: unknown) => setPlAds({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab, main.from, main.to, state.mall]);

  const [plAdsChart, setPlAdsChart] = useState<Loadable<PlAdsResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab !== "ads") return;
    setPlAdsChart({ status: "loading" });
    fetchPlAds(chart.from, chart.to, state.mall)
      .then((data) => setPlAdsChart({ status: "ok", data }))
      .catch((e: unknown) => setPlAdsChart({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab, chart.from, chart.to, state.mall]);

  // ---- 財務タブ（freee 試算表。管理会計の GET /pl とは別系統） ----
  const [plFinance, setPlFinance] = useState<Loadable<FinanceResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab !== "finance") return;
    setPlFinance({ status: "loading" });
    fetchPlFinance(state.month)
      .then((data) => setPlFinance({ status: "ok", data }))
      .catch((e: unknown) => setPlFinance({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab, state.month]);

  // ---- 取込タブ ----
  const [plImports, setPlImports] = useState<Loadable<PlImportsResponse>>({ status: "loading" });
  useEffect(() => {
    if (state.tab !== "import") return;
    setPlImports({ status: "loading" });
    fetchPlImports()
      .then((data) => setPlImports({ status: "ok", data }))
      .catch((e: unknown) => setPlImports({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [state.tab]);

  const set = (patch: Partial<PlState>) => setState((s) => ({ ...s, ...patch }));
  const setBasis = (basis: FinanceBasis) => {
    storeBasis(basis);
    set({ basis });
  };

  const meta = plMain.status === "ok" ? plMain.data.meta : null;
  const financeFetchedAt = plFinance.status === "ok" ? plFinance.data.meta.fetched_at : null;
  const updatedLabel = isFinance
    ? `${financeFetchedAt ? `freee 取得 ${new Date(financeFetchedAt).toLocaleString("ja-JP", { year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })} ・ ` : ""}freee 計上額`
    : meta
      ? `データ更新 ${new Date(meta.data_updated_at).toLocaleString("ja-JP", { year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })} ・ 税抜`
      : "税抜";

  return (
    <div className="pl-root">
      <div className="pl-shell">
        <div className="pl-header">
          <div className="pl-header-inner">
            <div className="pl-logo">
              <span className="pl-logo-word">STOQ</span>
              <span className="pl-logo-sub">管理会計</span>
            </div>
            <nav className="pl-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  className={`pl-tab-btn ${state.tab === t.key ? "active" : ""}`}
                  onClick={() => set({ tab: t.key })}
                >
                  {t.label}
                </button>
              ))}
            </nav>
            <span className="pl-updated">{updatedLabel}</span>
          </div>
        </div>

        {!isFinance && meta && (meta.estimates.length > 0 || meta.notes.length > 0) && (
          <div className="pl-notes">
            {meta.notes.map((n, i) => (
              <div className="pl-notes-row" key={`note-${i}`}>
                ・{n}
              </div>
            ))}
            {meta.estimates.length > 0 && (
              <div className="pl-notes-row">
                見込み計上中: {meta.estimates.map((e) => `${e.label}（${e.note}）`).join(" ／ ")}
              </div>
            )}
          </div>
        )}

        <div className="pl-body">
          <PlPeriodBar
            mode={state.mode}
            day={state.day}
            month={state.month}
            from={state.from}
            to={state.to}
            mall={state.mall}
            main={main}
            compare={compare}
            today={today}
            minDate={isFinance ? FINANCE_MIN_DATE : PL_MIN_DATE}
            showMallFilter={state.tab === "product" || state.tab === "ads"}
            allowedModes={isFinance ? FINANCE_MODES : undefined}
            onModeChange={(mode) => set({ mode })}
            onDayChange={(day) => set({ day })}
            onMonthChange={(month) => set({ month })}
            onFromChange={(from) => set({ from })}
            onToChange={(to) => set({ to })}
            onMallChange={(mall) => set({ mall })}
          />

          {state.tab === "overview" && (
            <OverviewTabGate plMain={plMain} plChart={plChartData} chart={chart} hoverDay={hoverDay} onHoverDay={setHoverDay} />
          )}
          {state.tab === "mall" && <MallTabGate plMain={plMain} />}
          {state.tab === "product" && <ProductTabGate data={plProducts} />}
          {state.tab === "ads" && <AdsTabGate main={plAds} chartData={plAdsChart} chart={chart} mall={state.mall} />}
          {state.tab === "finance" && <FinanceTabGate data={plFinance} basis={state.basis} onBasisChange={setBasis} />}
          {state.tab === "import" && <ImportTabGate data={plImports} />}

          <p className="pl-footer-note">
            {isFinance
              ? "財務タブは freee の月次試算表（制度会計）です。金額は freee 計上額（免税事業者の税込経理）で、管理会計の貢献利益（税抜）とは基準が異なります。"
              : "貢献利益 ＝ 売上 − 商品原価 − モール手数料 − 決済手数料 − 送料・物流費 − ポイント・クーポン原資 − 広告費。ROAS（全体）＝ 売上 ÷ 広告費、チャネル別ROAS ＝ 広告経由売上 ÷ 広告費。金額は税抜（原価は税込）。"}
          </p>
        </div>
      </div>
    </div>
  );
}

function OverviewTabGate({
  plMain,
  plChart,
  chart,
  hoverDay,
  onHoverDay,
}: {
  plMain: Loadable<PlResponse>;
  plChart: Loadable<PlResponse>;
  chart: { from: string; to: string };
  hoverDay: string | null;
  onHoverDay: (d: string | null) => void;
}) {
  if (plMain.status === "loading" || plChart.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (plMain.status === "error") return <StateBanner kind="error" message={plMain.message} />;
  if (plChart.status === "error") return <StateBanner kind="error" message={plChart.message} />;
  return <OverviewTab data={plMain.data} chartDaily={plChart.data.daily} chartFrom={chart.from} chartTo={chart.to} hoverDay={hoverDay} onHoverDay={onHoverDay} />;
}

function MallTabGate({ plMain }: { plMain: Loadable<PlResponse> }) {
  if (plMain.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (plMain.status === "error") return <StateBanner kind="error" message={plMain.message} />;
  return <MallTab data={plMain.data} />;
}

function ProductTabGate({ data }: { data: Loadable<PlProductsResponse> }) {
  if (data.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (data.status === "error") return <StateBanner kind="error" message={data.message} />;
  return <ProductTab data={data.data} />;
}

function AdsTabGate({
  main,
  chartData,
  chart,
  mall,
}: {
  main: Loadable<PlAdsResponse>;
  chartData: Loadable<PlAdsResponse>;
  chart: { from: string; to: string };
  mall: Mall | "all";
}) {
  if (main.status === "loading" || chartData.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (main.status === "error") return <StateBanner kind="error" message={main.message} />;
  if (chartData.status === "error") return <StateBanner kind="error" message={chartData.message} />;
  return <AdsTab data={main.data} chartDaily={chartData.data.daily} chartFrom={chart.from} chartTo={chart.to} mall={mall} />;
}

function FinanceTabGate({
  data,
  basis,
  onBasisChange,
}: {
  data: Loadable<FinanceResponse>;
  basis: FinanceBasis | null;
  onBasisChange: (b: FinanceBasis) => void;
}) {
  if (data.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (data.status === "error") return <StateBanner kind="error" message={data.message} />;
  return <FinanceTab data={data.data} basis={basis} onBasisChange={onBasisChange} />;
}

function ImportTabGate({ data }: { data: Loadable<PlImportsResponse> }) {
  if (data.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (data.status === "error") return <StateBanner kind="error" message={data.message} />;
  return <ImportTab data={data.data} />;
}
